/**
 * Minimal markdown renderer for AI Preview answers.
 *
 * The assistant responds in a small markdown subset (pipe tables, bold/italic,
 * inline code, lists, the odd heading and link). Rather than shipping a full
 * markdown library in the search bundle, this renders exactly that subset:
 * input is HTML-escaped first, transformed, then sanitized with DOMPurify as
 * the final safety net. Anything outside the subset degrades to plain text.
 */

import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'em',
  'code',
  'a',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
];

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Inline markdown on already-escaped text: code, bold, italic, safe links. */
const inline = (escaped: string): string =>
  escaped
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*\s][^*]*)\*/g, '<em>$1</em>')
    // Only http(s) and site-relative hrefs survive.
    .replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]*)\)/g,
      '<a href="$2">$1</a>'
    );

const isTableRow = (line: string): boolean =>
  line.trim().startsWith('|') && line.includes('|', 1);

const isTableSeparator = (line: string): boolean =>
  /^\s*\|?[\s:|-]+\|?\s*$/.test(line) && line.includes('-');

const splitCells = (line: string): string[] =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());

export const renderMarkdown = (markdown: string): string => {
  if (!markdown) return '';

  const lines = markdown.split('\n');
  const html: string[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushParagraph = () => {
    if (paragraph.length) {
      html.push(`<p>${inline(escapeHtml(paragraph.join(' ')))}</p>`);
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list) {
      const tag = list.ordered ? 'ol' : 'ul';
      html.push(
        `<${tag}>${list.items.map((i) => `<li>${i}</li>`).join('')}</${tag}>`
      );
      list = null;
    }
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      flushParagraph();
      flushList();
      continue;
    }

    // GFM pipe table: header row, separator row, body rows.
    if (
      isTableRow(trimmed) &&
      i + 1 < lines.length &&
      isTableSeparator(lines[i + 1])
    ) {
      flushParagraph();
      flushList();
      const header = splitCells(trimmed).map(
        (c) => `<th>${inline(escapeHtml(c))}</th>`
      );
      let j = i + 2;
      const body: string[] = [];
      while (j < lines.length && isTableRow(lines[j].trim())) {
        const cells = splitCells(lines[j].trim()).map(
          (c) => `<td>${inline(escapeHtml(c))}</td>`
        );
        body.push(`<tr>${cells.join('')}</tr>`);
        j += 1;
      }
      html.push(
        `<table><thead><tr>${header.join('')}</tr></thead>` +
          `<tbody>${body.join('')}</tbody></table>`
      );
      i = j - 1;
      continue;
    }

    const headingMatch = /^(#{1,4})\s+(.*)$/.exec(trimmed);
    if (headingMatch) {
      flushParagraph();
      flushList();
      const level = headingMatch[1].length;
      html.push(
        `<h${level}>${inline(escapeHtml(headingMatch[2]))}</h${level}>`
      );
      continue;
    }

    const bulletMatch = /^[-*•]\s+(.*)$/.exec(trimmed);
    const orderedMatch = /^\d+[.)]\s+(.*)$/.exec(trimmed);
    const listMatch = bulletMatch || orderedMatch;
    if (listMatch) {
      flushParagraph();
      const ordered = Boolean(orderedMatch);
      const item = inline(escapeHtml(listMatch[1]));
      if (!list || list.ordered !== ordered) {
        flushList();
        list = { ordered, items: [] };
      }
      list.items.push(item);
      continue;
    }

    flushList();
    paragraph.push(trimmed);
  }
  flushParagraph();
  flushList();

  const rendered = html.join('');
  // SSR safety: DOMPurify needs a DOM. Content only ever streams client-side,
  // but never emit unsanitized HTML if this is somehow reached on the server.
  if (typeof window === 'undefined' || !DOMPurify.isSupported) {
    return `<p>${escapeHtml(markdown)}</p>`;
  }
  return DOMPurify.sanitize(rendered, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ['href'],
  });
};

export default renderMarkdown;
